import _ from 'lodash';
import React from 'react';

import {
  Divider,
  styled,
  Typography
} from '@mui/material';
import {
  currencyUtil,
  BaseWidget
} from 'modules';

import { Table } from 'iron-forge-react-modules';
import * as types from './types';
import * as dataService from './dataService';



const TableComponent = Table.genTableComponent<types.IColumnId>({});
const FeeTableComponent = Table.genTableComponent<types.IFeeColumnId>();

const PriceTypography = styled(Typography, {
  shouldForwardProp: prop => !_.includes(['strikeThrough', 'isRefund'], prop)
})<{
  strikeThrough?: boolean;
  isRefund?: boolean;
}>(({ theme, strikeThrough, isRefund }) => ({
  fontSize: 12,
  color: isRefund ? theme.palette.success.main : '#6B788B',
  textDecoration: strikeThrough ? 'line-through' : undefined
}));



export interface IProps {
  orderId: string;
}

export const FC: React.FC<IProps> = props => {
  const queryTuple = dataService.useQuery(props.orderId);

  const tableRows = React.useMemo(
    () => _.map(queryTuple.data.lineItems, (lineItem): types.IRow => ({
      id: lineItem.id,
      columnId2Value: {
        sku: {
          type: 'string',
          label: lineItem.sku
        },
        itemTotal: {
          type: 'component',
          sortableValue: lineItem.itemTotalCents.plus(lineItem.taxTotalCents).toNumber(),
          label: (
            <>
              <PriceTypography strikeThrough={lineItem.refundAmountCents.gt(0)}>
                {currencyUtil.convertBigCentsToDollars(lineItem.itemTotalCents.plus(lineItem.feeTotalCents))}
              </PriceTypography>

              {lineItem.refundAmountCents.gt(0) && (
                <PriceTypography isRefund>
                  -{currencyUtil.convertBigCentsToDollars(lineItem.refundAmountCents)}
                </PriceTypography>
              )}
            </>
          )
        },
        taxTotal: {
          type: 'string',
          label: currencyUtil.convertBigCentsToDollars(lineItem.taxTotalCents)
        },
        discountTotal: {
          type: 'string',
          label: currencyUtil.convertBigCentsToDollars(lineItem.discountTotalCents)
        },
        productName: {
          type: 'string',
          label: lineItem.productName
        },
        quantity: {
          type: 'string',
          label: lineItem.quantity.toString()
        }
      }
    })),
    [queryTuple]
  );

  return (
    <BaseWidget.FC
      title="Product Information">
      <TableComponent
        disableSorting
        rows={tableRows}
        columns={[
          {
            id: 'productName',
            label: "Product"
          },
          {
            id: 'sku',
            label: "SKU"
          },
          {
            id: 'quantity',
            label: "Qty"
          },
          {
            id: 'itemTotal',
            label: "Subtotal"
          },
          {
            id: 'taxTotal',
            label: "Taxes"
          },
          {
            id: 'discountTotal',
            label: "Discount"
          }
        ]} />

      <Divider />

      <FeeTableComponent
        disableSorting
        columns={[
          {
            id: 'name',
            label: "Name",
            width: '80%'
          },
          {
            id: 'subtotal',
            label: "Subtotal"
          },
          {
            id: 'taxTotal',
            label: "Taxes"
          },
          {
            id: 'discountTotal',
            label: "Discount"
          }
        ]}
        rows={_.map(queryTuple.data.feeLines, (line): types.IFeeRow => ({
          id: line.id,
          columnId2Value: {
            name: {
              type: 'string',
              label: line.name
            },
            subtotal: {
              type: 'string',
              label: currencyUtil.convertBigCentsToDollars(line.subtotalCents)
            },
            taxTotal: {
              type: 'string',
              label: currencyUtil.convertBigCentsToDollars(line.taxTotalCents)
            },
            discountTotal: {
              type: 'string',
              label: "-"
            }
          }
        }))} />
    </BaseWidget.FC>
  );
};
